import { Box, Button, Paper, Slider, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import {
  AppSwitch,
  ConfirmDialog,
  FilterPills,
  HistoryTimeline,
  InlineAlert,
  LoadingButton,
  QueryErrorAlert,
  QueryStateView,
} from "src/grading/components";
import SettingRow from "src/grading/components/SettingRow/SettingRow";
import { usePermissions } from "src/grading/hooks/usePermissions";
import DistrictHeader from "src/grading/pages/Districts/DistrictHeader";
import { errorCodeOf, errorMessage } from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { effectivePrivacySettings } from "src/grading/utils/privacySettings";
import {
  OrganizationView,
  PrivacyImpact,
  PrivacyImpactChange,
  PrivacySettings,
  PrivacySettingsView,
} from "src/models/hierarchy";
import { HistoryPageArgs } from "src/models/history";
import { apiSlice } from "src/store/apiSlice";
import { useGetDistrictQuery } from "src/store/reducers/districts/districtsApiSlice";
import {
  useDeletePrivacyVideosMutation,
  useGetPrivacyHistoryQuery,
  useGetPrivacySettingsQuery,
  useLazyGetPrivacyImpactQuery,
  useUpdatePrivacySettingsMutation,
} from "src/store/reducers/districts/privacyApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import ImpactDialog from "./ImpactDialog";

type Expanding = "videoAllowed" | "studentLoginEnabled" | "shareDob";
/** What the acknowledgement dialog turns on: a toggle, or email delivery of letters. */
type AckTarget = Expanding | "letterEmail";
const EXPANDING: Expanding[] = [
  "videoAllowed",
  "studentLoginEnabled",
  "shareDob",
];
const TITLE_DEFAULTS: Record<Expanding, string> = {
  videoAllowed: "Video capture",
  studentLoginEnabled: "Student sign-in",
  shareDob: "Store birth dates",
};
const RESULT_YEARS = [1, 2, 3, 4, 5, 6, 7];
/** How often the clean-up status is re-read while a clean-up runs. */
const CLEANUP_POLL_MS = 5_000;
/** Stop waiting for a clean-up's first history entry after this long. */
const CLEANUP_WATCH_MS = 10 * 60_000;

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

interface ImpactState {
  changes: PrivacyImpactChange[];
  counts?: PrivacyImpact;
  loading: boolean;
  error?: string;
  /** Re-shown after the server refused the save with IMPACT_CHANGED; confirming saves. */
  changed: boolean;
}

/**
 * District privacy settings (contracts/console-district-tabs.md `privacy`). Turning on anything
 * that collects more data, email delivery included, needs an acknowledgement; turning off birth
 * dates or choosing print-only letters first shows how many records are removed. Held videos can
 * be deleted while video is off, and the history lists every change and clean-up.
 */
const PrivacySettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { districtId } = useParams<{ districtId: string }>();
  const { hasPermissions } = usePermissions();
  const privacy = useGetPrivacySettingsQuery(districtId);
  // Secondary: the header's name and meta. Its failure is said inline under the header (FR-048).
  const districtQuery = useGetDistrictQuery(districtId, {
    skip: !districtId,
  });
  const district = districtQuery.data;
  const canManage = hasPermissions(
    permissions.MANAGE_PRIVACY.action,
    permissions.MANAGE_PRIVACY.subject,
  );

  // Every district tab shares the district header and its tabs, so none shows a breadcrumb trail
  // (it overlapped the header's eyebrow).
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  return (
    <QueryStateView query={privacy}>
      {() => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <DistrictHeader districtId={districtId} district={district} />
          {districtQuery.isError && !district ? (
            <QueryErrorAlert
              query={districtQuery}
              data-testid="district-details-error"
            >
              {t("district.detailsLoadError")}
            </QueryErrorAlert>
          ) : null}
          <PrivacyForm
            view={privacy.data as PrivacySettingsView}
            districtId={districtId}
            district={district}
            canManage={canManage}
          />
        </Box>
      )}
    </QueryStateView>
  );
};

interface PrivacyFormProps {
  view: PrivacySettingsView;
  districtId: string;
  district?: OrganizationView;
  canManage: boolean;
}

const PrivacyForm = ({
  view,
  districtId,
  district,
  canManage,
}: PrivacyFormProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [update, { isLoading: saving }] = useUpdatePrivacySettingsMutation();
  const [fetchImpact] = useLazyGetPrivacyImpactQuery();
  const [deleteVideos, { isLoading: deletingVideos }] =
    useDeletePrivacyVideosMutation();

  const saved = useMemo(
    () => effectivePrivacySettings(view.settings),
    [view.settings],
  );
  const [baseline, setBaseline] = useState(saved);
  const [form, setForm] = useState<PrivacySettings>(saved);
  // A refetch re-seeds the form only when it has no unsaved edits.
  if (!same(saved, baseline)) {
    setBaseline(saved);
    if (same(form, baseline)) setForm(saved);
  }

  const [ack, setAck] = useState<AckTarget | null>(null);
  const [impact, setImpact] = useState<ImpactState | null>(null);
  const [confirmed, setConfirmed] = useState<{
    students?: number;
    contacts?: number;
  }>({});
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState(false);
  const [videosDialog, setVideosDialog] = useState(false);
  const [videosStarted, setVideosStarted] = useState(false);
  const [videosError, setVideosError] = useState<string | null>(null);

  // Clean-up status (FR-010): the latest clean-up history entry, and after a save or video delete
  // that started a job, "running" until that job's first entry arrives.
  const [watchFrom, setWatchFrom] = useState<{ id: string | null } | null>(
    null,
  );
  const [poll, setPoll] = useState(false);
  const cleanup = useGetPrivacyHistoryQuery(
    { districtId, category: ["cleanup"], limit: 1, offset: 0 },
    { pollingInterval: poll ? CLEANUP_POLL_MS : 0 },
  );
  const latestCleanup = cleanup.data?.items[0];
  const cleanupStatus = latestCleanup?.changes.find(
    (change) => change.field === "status",
  )?.to;
  const waitingForCleanup =
    watchFrom !== null && (latestCleanup?.id ?? null) === watchFrom.id;
  const cleanupRunning =
    waitingForCleanup ||
    cleanupStatus === "queued" ||
    cleanupStatus === "running";
  const cleanupStopped = !cleanupRunning && cleanupStatus === "failed";
  const wasRunning = useRef(cleanupRunning);
  useEffect(() => {
    setPoll(cleanupRunning);
    if (wasRunning.current && !cleanupRunning)
      // The clean-up finished: refresh the held-video count and the history list.
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "Privacy", id: districtId },
          { type: "DistrictHistory", id: districtId },
        ]),
      );
    wasRunning.current = cleanupRunning;
  }, [cleanupRunning, dispatch, districtId]);
  useEffect(() => {
    if (!watchFrom) return undefined;
    const timer = setTimeout(() => setWatchFrom(null), CLEANUP_WATCH_MS);
    return () => clearTimeout(timer);
  }, [watchFrom]);
  const watchCleanup = () => setWatchFrom({ id: latestCleanup?.id ?? null });

  const descriptions = view.dataFlowDescriptions;
  const districtName = district?.name;
  const dirty = !same(form, saved);
  const titleOf = (key: AckTarget) =>
    key === "letterEmail"
      ? t("privacy.setting.letterEmail")
      : t(`privacy.setting.${key}`, { defaultValue: TITLE_DEFAULTS[key] });
  const yearsLabel = (y: number) =>
    y === 1
      ? t("privacy.oneSchoolYear", { defaultValue: "1 school year" })
      : t("privacy.schoolYears", {
          defaultValue: "{{years}} school years",
          years: y,
        });
  const saveErrorText = t("privacy.saveError", {
    defaultValue: "Could not save privacy settings",
  });

  /** The settings this form turns on that collect more data, so the save is acknowledged. */
  const needsAck = (f: PrivacySettings): AckTarget[] => [
    ...EXPANDING.filter((k) => f[k] && !saved[k]),
    ...(f.letterDelivery === "email" && saved.letterDelivery === "print"
      ? (["letterEmail"] as const)
      : []),
  ];
  /** The changes in this form that remove data, so the save carries confirmed counts. */
  const removals = (f: PrivacySettings): PrivacyImpactChange[] => [
    ...(saved.shareDob && !f.shareDob ? (["shareDob:false"] as const) : []),
    ...(saved.letterDelivery === "email" && f.letterDelivery === "print"
      ? (["letterDelivery:print"] as const)
      : []),
  ];

  const edit = (next: PrivacySettings) => {
    setForm(next);
    setSavedMessage(false);
    setError(null);
  };

  const loadImpact = async (
    changes: PrivacyImpactChange[],
    changed: boolean,
  ) => {
    setImpact({ changes, loading: true, changed });
    const settle = (patch: Partial<ImpactState>) =>
      setImpact((current) =>
        current?.changes === changes
          ? { ...current, ...patch, loading: false }
          : current,
      );
    try {
      const results = await Promise.all(
        changes.map((change) => fetchImpact({ districtId, change }).unwrap()),
      );
      settle({ counts: Object.assign({}, ...results), error: undefined });
    } catch (e) {
      settle({
        error: errorMessage(t, e, t("privacy.impact.countError")),
      });
    }
  };

  const save = async (
    f: PrivacySettings = form,
    c: typeof confirmed = confirmed,
  ) => {
    setError(null);
    setSavedMessage(false);
    const changes = removals(f);
    const confirmedImpact = {
      ...(changes.includes("shareDob:false") && c.students !== undefined
        ? { students: c.students }
        : {}),
      ...(changes.includes("letterDelivery:print") && c.contacts !== undefined
        ? { contacts: c.contacts }
        : {}),
    };
    try {
      const result = await update({
        districtId,
        body: {
          ...f,
          acknowledged: needsAck(f).length > 0,
          ...(Object.keys(confirmedImpact).length > 0
            ? { confirmedImpact }
            : {}),
        },
      }).unwrap();
      setImpact(null);
      setConfirmed({});
      setSavedMessage(true);
      if (result?.cleanupJobId) watchCleanup();
    } catch (e) {
      if (errorCodeOf(e) === "IMPACT_CHANGED" && changes.length > 0) {
        // The counts moved since they were confirmed: show the new ones and confirm again.
        void loadImpact(changes, true);
        return;
      }
      setImpact(null);
      setError(errorMessage(t, e, saveErrorText));
    }
  };

  const toggle = (key: Expanding, value: boolean) => {
    if (value && !saved[key]) {
      setAck(key);
      return;
    }
    if (key === "shareDob" && !value && saved.shareDob) {
      void loadImpact(["shareDob:false"], false);
      return;
    }
    edit({ ...form, [key]: value });
  };

  const chooseDelivery = (value: PrivacySettings["letterDelivery"]) => {
    if (value === form.letterDelivery) return;
    if (value === "email" && saved.letterDelivery === "print") {
      setAck("letterEmail");
      return;
    }
    if (value === "print" && saved.letterDelivery === "email") {
      void loadImpact(["letterDelivery:print"], false);
      return;
    }
    edit({ ...form, letterDelivery: value });
  };

  const confirmImpact = () => {
    if (!impact?.counts) return;
    const next = { ...form };
    const nextConfirmed = { ...confirmed };
    if (impact.changes.includes("shareDob:false")) {
      next.shareDob = false;
      nextConfirmed.students = impact.counts.students ?? 0;
    }
    if (impact.changes.includes("letterDelivery:print")) {
      next.letterDelivery = "print";
      nextConfirmed.contacts = impact.counts.contacts ?? 0;
    }
    setConfirmed(nextConfirmed);
    if (impact.changed) {
      setForm(next);
      void save(next, nextConfirmed);
      return;
    }
    edit(next);
    setImpact(null);
  };

  const confirmVideoDelete = async () => {
    setVideosError(null);
    try {
      await deleteVideos(districtId).unwrap();
      setVideosStarted(true);
      watchCleanup();
    } catch (e) {
      setVideosError(errorMessage(t, e, t("privacy.videosDeleteError")));
    }
    setVideosDialog(false);
  };

  const row = (key: Expanding) => (
    <SettingRow
      key={key}
      id={`${key}-title`}
      title={titleOf(key)}
      description={descriptions[key]}
      control={(labelProps) => (
        <AppSwitch
          aria-labelledby={labelProps["aria-labelledby"]}
          inputProps={
            {
              "data-testid": `toggle-${key}`,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
          checked={form[key]}
          onChange={(e) => toggle(key, e.target.checked)}
          disabled={!canManage}
        />
      )}
    />
  );

  const fieldLabel = (text: string, id?: string) => (
    <Typography id={id} sx={{ fontSize: 15, fontWeight: 800 }}>
      {text}
    </Typography>
  );
  const note = (text: string) => (
    <Typography
      sx={(theme) => ({
        fontSize: 12.5,
        fontWeight: 500,
        color: theme.palette.tokens.textMuted,
      })}
    >
      {text}
    </Typography>
  );

  const videos = view.videos;
  // Held videos are shown while video is off (FR-002); retention keeps deleting them either way.
  const heldCount = saved.videoAllowed ? 0 : (videos?.count ?? 0);
  const showVideosHeld = heldCount > 0;
  const lastExpires = videos?.lastExpiresAt
    ? new Date(videos.lastExpiresAt)
    : null;
  const emailUnavailable =
    form.letterDelivery === "email" &&
    view.capabilities?.letterEmail === "not-configured";
  const useHistoryPage = (page: Required<HistoryPageArgs>) =>
    useGetPrivacyHistoryQuery({ districtId, ...page });

  return (
    <>
      <Box
        sx={(theme) => ({
          px: 2.25,
          py: 1.75,
          borderRadius: `${radius.panel}px`,
          bgcolor: theme.palette.tokens.primarySoft,
          color: theme.palette.text.primary,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.45,
        })}
      >
        {t("privacy.defaultsNote", {
          defaultValue:
            "The defaults are the most protective choice. Anything that collects more data shows exactly what it turns on, and needs your acknowledgement.",
        })}
      </Box>

      {cleanupRunning ? (
        <Box data-testid="cleanup-running">
          <InlineAlert severity="info">
            {t("privacy.cleanupRunning")}
          </InlineAlert>
        </Box>
      ) : null}
      {cleanupStopped ? (
        <Box data-testid="cleanup-failed">
          <InlineAlert severity="error">
            {t("privacy.cleanupStopped")}
          </InlineAlert>
        </Box>
      ) : null}
      {/* FR-048: an unreadable clean-up status is said, not hidden as "nothing running". */}
      {cleanup.isError ? (
        <QueryErrorAlert query={cleanup} data-testid="cleanup-status-error">
          {t("privacy.cleanupStatusError")}
        </QueryErrorAlert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          component="section"
          aria-label={t("privacy.dataCollection", {
            defaultValue: "Data collection",
          })}
          sx={{ px: 2.75, py: 1 }}
        >
          {row("videoAllowed")}
          <Stack
            spacing={1.25}
            sx={(theme) => ({
              py: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Stack direction="row" justifyContent="space-between">
              {fieldLabel(
                t("privacy.videoRetention", {
                  defaultValue: "Video retention",
                }),
                "videoRetentionDays-label",
              )}
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  fontStretch: fontStretch.condensed,
                }}
              >
                {t("privacy.days", {
                  defaultValue: "{{days}} days",
                  days: form.videoRetentionDays,
                })}
              </Typography>
            </Stack>
            <Slider
              data-testid="video-retention-slider"
              aria-labelledby="videoRetentionDays-label"
              min={1}
              max={30}
              step={1}
              value={form.videoRetentionDays}
              onChange={(_e, v) =>
                edit({ ...form, videoRetentionDays: v as number })
              }
              disabled={!canManage}
              valueLabelDisplay="auto"
            />
            {note(
              t("privacy.autoDelete", {
                defaultValue:
                  "Videos are deleted automatically after this many days. The maximum is 30.",
              }),
            )}
            {showVideosHeld ? (
              <Box
                data-testid="videos-held"
                role="group"
                aria-labelledby="videos-held-title"
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: 1.75,
                  borderRadius: `${radius.panel}px`,
                  bgcolor: theme.palette.surfaceAlt,
                })}
              >
                <Typography
                  id="videos-held-title"
                  sx={{ fontSize: 14, fontWeight: 800 }}
                >
                  {t("privacy.videosHeldTitle")}
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  {t("privacy.videosHeld", { count: heldCount })}
                  {lastExpires && !Number.isNaN(lastExpires.getTime())
                    ? ` ${t("privacy.videosLastExpires", {
                        date: lastExpires.toLocaleDateString(i18n.language, {
                          dateStyle: "medium",
                        }),
                      })}`
                    : null}
                </Typography>
                {canManage ? (
                  <Box>
                    <Button
                      variant="outlined"
                      color="error"
                      data-testid="videos-delete"
                      onClick={() => setVideosDialog(true)}
                      sx={{ minHeight: 44 }}
                    >
                      {t("privacy.videosDelete")}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            {videosStarted ? (
              <InlineAlert severity="info">
                {t("privacy.videosDeleteStarted")}
              </InlineAlert>
            ) : null}
            {videosError ? (
              <InlineAlert severity="error">{videosError}</InlineAlert>
            ) : null}
          </Stack>
          {row("studentLoginEnabled")}
          <Box sx={{ "& > div": { borderBottom: 0 } }}>{row("shareDob")}</Box>
        </Paper>

        <Paper
          component="section"
          aria-label={t("privacy.recordsAndLetters", {
            defaultValue: "Records and letters",
          })}
          sx={{ px: 2.75, py: 2.5 }}
        >
          <Stack spacing={2.25}>
            <Stack spacing={1.25}>
              {fieldLabel(
                t("privacy.keepResults", { defaultValue: "Keep results for" }),
              )}
              {canManage ? (
                <Box
                  sx={{
                    "& .MuiToggleButtonGroup-root": {
                      flexWrap: "wrap",
                      gap: 0.75,
                    },
                  }}
                >
                  <FilterPills
                    size="sm"
                    data-testid="results-years"
                    aria-label={t("privacy.keepResults", {
                      defaultValue: "Keep results for",
                    })}
                    options={RESULT_YEARS.map((y) => ({
                      value: String(y),
                      label: yearsLabel(y),
                    }))}
                    value={String(form.resultsRetentionYears)}
                    onChange={(v) =>
                      edit({ ...form, resultsRetentionYears: Number(v) })
                    }
                  />
                </Box>
              ) : (
                <Typography
                  data-testid="results-years"
                  sx={{ fontWeight: 700 }}
                >
                  {yearsLabel(form.resultsRetentionYears)}
                </Typography>
              )}
              {note(descriptions.resultsRetentionYears)}
            </Stack>

            <Stack spacing={1.25}>
              {fieldLabel(
                t("privacy.letterDelivery", {
                  defaultValue: "Family letter delivery",
                }),
              )}
              {canManage ? (
                <FilterPills
                  size="sm"
                  data-testid="letter-delivery"
                  aria-label={t("privacy.letterDelivery", {
                    defaultValue: "Family letter delivery",
                  })}
                  options={[
                    {
                      value: "print",
                      label: t("privacy.letterPrintOnly", {
                        defaultValue: "Print only",
                      }),
                    },
                    {
                      value: "email",
                      label: t("privacy.letterEmailGuardians", {
                        defaultValue: "Email guardians from roster",
                      }),
                    },
                  ]}
                  value={form.letterDelivery}
                  onChange={(v) =>
                    chooseDelivery(v as PrivacySettings["letterDelivery"])
                  }
                />
              ) : (
                <Typography
                  data-testid="letter-delivery"
                  sx={{ fontWeight: 700 }}
                >
                  {form.letterDelivery === "email"
                    ? t("privacy.letterEmailGuardians", {
                        defaultValue: "Email guardians from roster",
                      })
                    : t("privacy.letterPrintOnly", {
                        defaultValue: "Print only",
                      })}
                </Typography>
              )}
              {note(descriptions.letterDelivery)}
              {emailUnavailable ? (
                <Box data-testid="email-unavailable">
                  <InlineAlert severity="warning">
                    {t("privacy.emailUnavailable")}
                  </InlineAlert>
                </Box>
              ) : null}
            </Stack>

            {error && <InlineAlert severity="error">{error}</InlineAlert>}
            {savedMessage && (
              <InlineAlert severity="success">
                {t("privacy.saved", {
                  defaultValue: "Privacy settings saved and audited.",
                })}
              </InlineAlert>
            )}
            {canManage && (
              <Box>
                <LoadingButton
                  variant="contained"
                  data-testid="privacy-save"
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => void save()}
                  label={t("privacy.save", {
                    defaultValue: "Save privacy settings",
                  })}
                />
              </Box>
            )}
          </Stack>
        </Paper>
      </Box>

      <Paper
        component="section"
        aria-labelledby="privacy-history-title"
        data-testid="privacy-history"
        sx={{ px: 2.75, py: 2.5 }}
      >
        <Typography
          id="privacy-history-title"
          component="h2"
          sx={{ fontSize: 18, fontWeight: 800 }}
        >
          {t("privacy.historyTitle")}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
          {t("privacy.historyNote")}
        </Typography>
        <HistoryTimeline
          subjectName={districtName ?? ""}
          usePage={useHistoryPage}
          label={t("privacy.historyTitle")}
        />
      </Paper>

      <ConfirmDialog
        open={!!ack}
        testIdPrefix="ack"
        eyebrow={t("privacy.collectsMore", {
          defaultValue: "This collects more data",
        })}
        title={
          ack
            ? t("privacy.turnOn", {
                setting: titleOf(ack),
                defaultValue: "Turn on {{setting}}?",
              })
            : ""
        }
        body={
          ack === "letterEmail"
            ? t("privacy.emailFlow")
            : ack
              ? descriptions[ack]
              : ""
        }
        acknowledgement={t("privacy.acknowledge", {
          district:
            districtName ??
            t("privacy.yourDistrict", { defaultValue: "your district" }),
          defaultValue:
            "I understand what this turns on for every school in {{district}}, and it will be recorded in the district audit log.",
        })}
        cancelLabel={t("privacy.keepOff", { defaultValue: "Keep it off" })}
        confirmLabel={t("privacy.turnOnConfirm", { defaultValue: "Turn on" })}
        onConfirm={() => {
          if (ack === "letterEmail") edit({ ...form, letterDelivery: "email" });
          else if (ack) edit({ ...form, [ack]: true });
          setAck(null);
        }}
        onClose={() => setAck(null)}
      />

      <ImpactDialog
        open={!!impact}
        changes={impact?.changes ?? []}
        counts={impact?.counts}
        loading={impact?.loading}
        error={impact?.error}
        changed={impact?.changed}
        saving={saving}
        onRetry={
          impact
            ? () => void loadImpact(impact.changes, impact.changed)
            : undefined
        }
        onConfirm={confirmImpact}
        onClose={() => setImpact(null)}
      />

      <ConfirmDialog
        open={videosDialog}
        testIdPrefix="videos-delete"
        tone="destructive"
        eyebrow={t("privacy.impact.eyebrow")}
        title={t("privacy.videosDeleteTitle")}
        body={t("privacy.videosDeleteBody", { count: heldCount })}
        acknowledgement={t("privacy.videosDeleteAck")}
        confirmLabel={t("privacy.videosDeleteConfirm")}
        loading={deletingVideos}
        onConfirm={() => void confirmVideoDelete()}
        onClose={() => setVideosDialog(false)}
      />
    </>
  );
};

export default PrivacySettingsPage;
